// Compact contextual Radio state for the existing Voice button hold gesture.
// Not a page, not a nav item, not a second button — just the minimum feedback
// so the user knows Radio (not DAR) is live, and on which site.
import { Loader2, Radio as RadioIcon } from "lucide-react";
import { formatElapsed } from "@/lib/radio/radio-state";

export function VoiceRadioOverlay({
  visible,
  siteName,
  phase,
  elapsedMs,
  liveTranscript,
  transcriptionAvailable,
  participantCount,
  errorMessage,
  onDismissError,
}: {
  visible: boolean;
  siteName: string | null;
  phase: string;
  elapsedMs: number;
  liveTranscript: string;
  transcriptionAvailable: boolean;
  participantCount: number;
  errorMessage: string | null;
  onDismissError: () => void;
}) {
  if (!visible && !errorMessage) return null;

  const transmitting = phase === "transmitting";
  const status = errorMessage
    ? errorMessage
    : transmitting
      ? "TRANSMITTING"
      : phase === "stopping"
        ? "Finishing transmission"
        : "Connecting Radio";

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-3 bottom-24 z-50 mx-auto max-w-sm rounded-xl border border-[#CC0000]/50 bg-background/90 px-3 py-2 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.85)] backdrop-blur-md md:inset-x-auto md:right-6 md:bottom-28"
    >
      <div className="flex items-center gap-2 text-xs font-semibold">
        <RadioIcon className="h-4 w-4 text-[#CC0000]" />
        <span>RADIO</span>
        {siteName && <span className="truncate text-muted-foreground">{siteName}</span>}
        {transmitting && (
          <span className="ml-auto font-mono text-[#CC0000]">{formatElapsed(elapsedMs)}</span>
        )}
      </div>
      <p
        className={`mt-1 flex items-center gap-1.5 text-[11px] ${
          errorMessage ? "text-destructive" : transmitting ? "text-[#CC0000]" : "text-muted-foreground"
        }`}
      >
        {!errorMessage && !transmitting && phase !== "stopping" && (
          <Loader2 className="h-3 w-3 animate-spin" />
        )}
        {status}
      </p>
      {!errorMessage && (
        <>
          <p className="mt-1 text-[11px] text-muted-foreground">
            {liveTranscript ||
              (transmitting
                ? transcriptionAvailable
                  ? "Listening…"
                  : "Live transcription unavailable — audio is still transmitting"
                : "Hold to keep transmitting")}
          </p>
          <p className="mt-1 text-[10px] text-muted-foreground">
            {participantCount} on channel · Release to stop
          </p>
        </>
      )}
      {errorMessage && (
        <button
          type="button"
          onClick={onDismissError}
          className="pointer-events-auto mt-1 text-[10px] underline"
        >
          Dismiss
        </button>
      )}
    </div>
  );
}
